import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import BreadCrumb from '../../../../components/BreadCrumb/BreadCrumb';
import CustomStepper from '../../../../components/CustomStepper/CustomStepper';
import GroupInformationForm from '../../../../components/Operator/Group/Forms/InformationForm/GroupInformationForm';
import GroupRatesForm from '../../../../components/Operator/Group/Forms/RatesForm/GroupRatesForm';
import GroupBillingForm from '../../../../components/Operator/Group/Forms/Billing/GroupBillingForm';
import axiosWrapper from '../../../../utils/api';
import { API_URL } from '../../../../utils/apiUrl';
import './EditGroup.scss';

const EditGroup = () => {
    const { groupId } = useParams();
    const { userToken } = useSelector((state) => state.auth);
    const [locationOptions, setLocationOptions] = useState([]);

    // Step state for the multi-step form
    const [currentStep, setCurrentStep] = useState(0);

    // State for storing the fetched group admin data
    const [groupData, setGroupData] = useState(null);

    // Step 0 – Group basic information
    const [step0Values, setStep0Values] = useState({
        groupName: '',
        streetAddress: '',
        streetAddress2: '',
        city: '',
        state: '',
        zipCode: '',
        firstName: '',
        lastName: '',
        title: '',
        email: '',
        phoneNumber: '',
        notifications: false,
        isCompleted: false
    });

    // Step 1 – Group subscriptions & validations
    const [step1Values, setStep1Values] = useState({
        subscriptions: [],
        validations: [],
        isCompleted: false
    });

    // Step 2 – Billing / Terms
    const [step2Values, setStep2Values] = useState({
        autoPay: false,
        invoicing: false,
        netTerms: '',
        processingFee: ''
    });

    // Fetch single group admin details by ID
    const getGroupAdmin = async () => {
        const response = await axiosWrapper('get', API_URL.GET_GROUP_ADMIN_BY_ID(groupId), {}, userToken);
        if (response && response.data) {
            setGroupData(response.data);
        }
    };

    useEffect(() => {
        getGroupAdmin();
    }, [groupId, userToken]);

    // When groupData is fetched, update the step values accordingly
    useEffect(() => {
        if (groupData) {
            setStep0Values({
                groupName: groupData.groupName || '',
                streetAddress: groupData.streetAddress || '',
                streetAddress2: groupData.streetAddress2 || '',
                city: groupData.city || '',
                state: groupData.state || '',
                zipCode: groupData.zipCode || '',
                firstName: groupData.firstName || '',
                lastName: groupData.lastName || '',
                title: groupData.title || '',
                email: groupData.email || '',
                phoneNumber: groupData.phoneNumber || '',
                notifications: groupData.notifications || false,
                isCompleted: true
            });

            const formatValidations = groupData?.validations.map((val) => ({
                zoneValidation: val.locationId || '',
                discount: val.discount || 0,
                maximumDuration: val.isHourlyMaximumDuration ? val.maximumDuration / 60 : val.maximumDuration || 0,
                monthlyMaximum: val.monthlyMaximum || 0,
                isFixedPrice: val.isFixedPrice || false,
                isHourlyMaximumDuration: val.isHourlyMaximumDuration || false,
                _id: val._id || ''
            }));

            // Map subscriptions from the aggregated result so that the form fields are available
            const formatSubscriptions = (groupData.subscriptions || []).map((sub) => ({
                // Map location id to zone for select field
                zone: sub.location?._id || sub.location || '',
                // Map recurring rate id to subscription field. You can also use an "originalRate" field if needed.
                subscription: sub.rate || '',
                // For the special rate, use metadata.specialRate if available; otherwise, derive from the amount (cents to dollars)
                specialRate: sub.metadata?.specialRate || (sub.amount ? sub.amount / 100 : ''),
                parkerMinimum: sub.metadata?.parkerMinimum || '',
                parkerMaximum: sub.metadata?.parkerMaximum || '',
                originalRate: sub.rate || ''
            }));

            setStep1Values({
                subscriptions: formatSubscriptions || [],
                validations: formatValidations || [],
                isCompleted: true
            });
            setStep2Values({
                autoPay: groupData.terms?.autoPay || false,
                invoicing: groupData.terms?.invoicing || false,
                netTerms: groupData.terms?.netTerms || '',
                processingFee: groupData.terms?.processingFee || ''
            });
        }
    }, [groupData]);

    // Fetch location options for zone selection (used in the GroupRatesForm)
    useEffect(() => {
        const fetchLocations = async () => {
            const { data } = await axiosWrapper('GET', API_URL.GET_LOCATIONS, {}, userToken);
            const options = data?.map((location) => ({
                value: location?._id || '',
                label: location?.zone || '',
                recurringRates: location?.recurringRates || [] // For dynamic subscription mapping
            }));
            setLocationOptions(options);
        };
        fetchLocations();
    }, [userToken]);

    // Function to navigate between steps in the multi-step form
    const changeCurrentStep = (nextStep, conditionalChange = true) => {
        if (conditionalChange) {
            if (nextStep === 0 && currentStep !== 0) {
                setStep0Values((prev) => ({ ...prev, isCompleted: false }));
                setCurrentStep(nextStep);
            } else if (nextStep === 1 && step0Values.isCompleted) {
                setStep1Values((prev) => ({ ...prev, isCompleted: false }));
                setCurrentStep(nextStep);
            } else if (nextStep === 2 && step0Values.isCompleted && step1Values.isCompleted) {
                setStep2Values((prev) => ({ ...prev, isCompleted: false }));
                setCurrentStep(nextStep);
            }
        } else {
            setCurrentStep(nextStep);
        }
    };

    // Scroll to top when step changes
    useEffect(() => {
        document.getElementById('scrollable-div')?.scrollTo(0, 0);
    }, [currentStep]);

    return (
        <>
            <Helmet>
                <title>Edit Group | Level Parking</title>
            </Helmet>
            <BreadCrumb items={[{ label: 'Groups', href: '/admin/groups' }, { label: 'Edit Group' }]} />
            <div className="edit-group-form-container">
                <CustomStepper currentStep={currentStep} changeCurrentStep={changeCurrentStep} />
                {currentStep === 0 && (
                    <GroupInformationForm
                        changeCurrentStep={changeCurrentStep}
                        step0Values={step0Values}
                        setStep0Values={setStep0Values}
                    />
                )}
                {currentStep === 1 && (
                    <GroupRatesForm
                        changeCurrentStep={changeCurrentStep}
                        step1Values={step1Values}
                        setStep1Values={setStep1Values}
                        zoneOptions={locationOptions}
                    />
                )}
                {currentStep === 2 && (
                    <GroupBillingForm
                        changeCurrentStep={changeCurrentStep}
                        step2Values={step2Values}
                        setStep2Values={setStep2Values}
                        step1Values={step1Values}
                        step0Values={step0Values}
                    />
                )}
            </div>
        </>
    );
};

export default EditGroup;
